import type { AuthenticationRequest, AuthenticationResult } from '../../auth/models/auth.model';
import { LegacyAuthModalState } from '../../auth/models/auth.model';
import { authenticationFlowService } from '../../auth/services/authentication-flow.service';
import { modalService } from '../../modal/modal.service';
import type { AppDispatch } from '../../store';
import { store } from '../../store';
import type {
  ConfirmTermsResult,
  Membership,
  MembershipJoinRequest,
} from '../models/membership.model';
import { setJoinInProgress, setMembershipJoinRequest } from '../store/membership.reducer';
import { addMembershipToCart, createContract } from '../store/membership.thunk';

/**
 * Plain TypeScript service for membership join flow orchestration
 *
 * Replaces: src/app/membership/membership-join-flow.service.ts (Angular)
 *
 * This service handles:
 * - Join flow orchestration
 * - Business logic validation
 * - localStorage persistence
 * - Dispatching final actions
 *
 * This is NOT a Redux thunk because:
 * - No async operations (no HTTP calls)
 * - No store queries (data passed as parameters)
 * - Just orchestration and business logic
 */
export class MembershipJoinFlowService {
  /**
   * Initiate membership join flow
   *
   * Replaces: MembershipJoinFlowService.initiateMembershipJoin()
   *
   * Called when: User clicks "Join" button on a tier
   *
   * Flow:
   * 1. Store request in localStorage (for recovery after auth redirect)
   * 2. Store request in Redux (for immediate access)
   * 3. Trigger authentication flow (opens modal or redirects to Shopify)
   *
   * @param request - The membership join request with tier info
   * @param dispatch - Redux dispatch function
   * @param openAuthModal - Function to open authentication modal (from component)
   */
  static initiateMembershipJoin(
    request: MembershipJoinRequest,
    dispatch: AppDispatch,
    openAuthModal: (request: AuthenticationRequest) => void
  ): void {
    // Check if user is already authenticated by importing store
    const state = store.getState();
    const isAuthenticated = state.auth.loggedIn;

    // Store in localStorage for recovery after auth redirect
    this.storeMembershipJoinRequest(request);

    // Store in Redux for immediate access
    dispatch(setMembershipJoinRequest(request));

    // Only trigger auth flow if user is NOT already authenticated
    // If authenticated, the setMembershipJoinRequest listener will handle join processing
    if (!isAuthenticated) {
      authenticationFlowService.initiateAuthentication({
        state: LegacyAuthModalState.CREATE_ACCOUNT,
        terms: request.tier.legalTerms,
        preventLoad: true,
        rejectUrl: request.rejectUrl,
      });
    } else {
      console.log('[MembershipJoinFlow] User authenticated, skipping auth flow');
    }
  }

  /**
   * Process membership join after authentication completes
   *
   * Replaces: MembershipJoinFlowService.processMembershipJoin()
   *
   * Called when: All prerequisites are met (auth + memberships + join request)
   *
   * Flow:
   * 1. Check if user already has contract → redirect if yes
   * 2. Check if legal terms modal needed
   * 3. If needed → show modal → await user response
   * 4. If accepted (or not needed) → finalize join
   * 5. If rejected → redirect to reject URL
   *
   * @param authenticationResult - Result from authentication flow
   * @param request - The membership join request
   * @param memberships - User's current memberships (to check for existing contracts)
   * @param dispatch - Redux dispatch function
   * @param openLegalTermsModal - Function to open legal terms modal (from component)
   */
  static async processMembershipJoin(
    authenticationResult: AuthenticationResult,
    request: MembershipJoinRequest,
    memberships: Membership[],
    dispatch: AppDispatch,
    openLegalTermsModal?: (terms: string) => Promise<ConfirmTermsResult>
  ): Promise<void> {
    dispatch(setJoinInProgress(true));

    // Check if user already has contract for this tier
    if (this.userHasContractForTier(memberships, request.tier.id)) {
      // User already subscribed to this tier — terminal outcome, drop the stored request
      this.clearStoredMembershipJoinRequest();
      dispatch(setJoinInProgress(false));
      if (request.redirectUrl) {
        window.location.href = request.redirectUrl;
      }
      return;
    }

    // Check if legal terms modal is needed
    const needsLegalTerms =
      !!request.tier.legalTerms &&
      request.tier.legalTerms.trim() !== '' &&
      !authenticationResult.termsAccepted;

    if (needsLegalTerms) {
      // Legal terms needed
      if (!openLegalTermsModal) {
        console.error('Legal terms modal function not provided');
        return;
      }
      try {
        // Show modal and wait for user response
        const result = await openLegalTermsModal(request.tier.legalTerms);
        if (result.termsAccepted) {
          // User accepted terms
          this.finalizeMembershipJoin(request, dispatch, result.optInToMarketingEmails);
        } else {
          // User rejected terms — terminal outcome
          this.clearStoredMembershipJoinRequest();
          dispatch(setJoinInProgress(false));
          if (request.rejectUrl) {
            window.location.href = request.rejectUrl;
          }
        }
      } catch (error) {
        // Modal dismissed (clicked outside, ESC key, etc.) — terminal outcome
        this.clearStoredMembershipJoinRequest();
        dispatch(setJoinInProgress(false));
        if (request.rejectUrl) {
          window.location.href = request.rejectUrl;
        } else {
          console.error('No rejectUrl found on legal terms modal error:', error);
          modalService.closeAll();
        }
      }
    } else {
      // No legal terms needed (either no terms or already accepted during auth)
      this.finalizeMembershipJoin(request, dispatch, authenticationResult.optInToMarketingEmails);
    }
  }

  /**
   * Process stored membership join request from localStorage
   *
   * Replaces: MembershipJoinFlowService.processStoredMembershipJoinRequest()
   *
   * Called when:
   * - After memberships load (by listener)
   * - After authentication completes (by listener)
   * - After page reload/redirect
   *
   * Flow:
   * 1. Read from localStorage
   * 2. If exists → remove from localStorage
   * 3. Dispatch setMembershipJoinRequest to trigger join flow
   *
   * @param dispatch - Redux dispatch function
   */
  static processStoredMembershipJoinRequest(dispatch: AppDispatch): void {
    // The stored request is intentionally NOT removed here: it must survive a
    // mid-flow refresh so the join can resume (SNGL-6967). It is cleared only on
    // a terminal outcome (contract created / already member / cart add / failure).
    const request = this.consumeStoredMembershipJoinRequest();
    if (request) {
      dispatch(setJoinInProgress(true));
      // Store in Redux to trigger the join flow
      // Listeners and component useEffect will react to this
      dispatch(setMembershipJoinRequest(request));
    }
  }

  // Guards against the stored request being processed more than once per page
  // load (multiple loadMemberships.fulfilled + authenticationResult can race)
  // now that it stays in localStorage until a terminal outcome.
  private static storedRequestConsumed = false;

  /**
   * Return the stored join request at most once per page load, without
   * deleting it from localStorage.
   */
  static consumeStoredMembershipJoinRequest(): MembershipJoinRequest | null {
    if (this.storedRequestConsumed) {
      return null;
    }
    const request = this.getStoredMembershipJoinRequest();
    if (request) {
      this.storedRequestConsumed = true;
    }
    return request;
  }

  // ==================== PRIVATE HELPER METHODS ====================

  /**
   * Finalize membership join - dispatch final action
   *
   * Replaces: MembershipService.processMembershipJoin()
   *
   * This method determines whether to:
   * - Create contract (free tier)
   * - Add to cart (paid tier)
   *
   * @param request - The membership join request
   * @param dispatch - Redux dispatch function
   * @param optInToMarketingEmails - Whether user opted in to marketing emails
   */
  private static finalizeMembershipJoin(
    request: MembershipJoinRequest,
    dispatch: AppDispatch,
    optInToMarketingEmails?: boolean
  ): void {
    // Validate tier has terms
    if (!request.tier?.terms || request.tier.terms.length < 1) {
      console.error('Invalid membership join request: no terms available');
      this.clearStoredMembershipJoinRequest();
      dispatch(setJoinInProgress(false));
      return;
    }

    // Find appropriate term (specified term ID or default to first)
    let term = request.tier.terms[0];
    if (request.termId) {
      const specifiedTerm = request.tier.terms.find((t) => t.id === request.termId);
      if (specifiedTerm) {
        term = specifiedTerm;
      }
    }

    if (!term) {
      console.error('Invalid membership join request: term not found');
      this.clearStoredMembershipJoinRequest();
      dispatch(setJoinInProgress(false));
      return;
    }

    // Dispatch based on tier type
    if (request.tier.freeTier) {
      // Free tier: create contract directly
      dispatch(
        createContract({
          tierId: request.tier.id,
          termId: term.id,
          redirectUrl: request.redirectUrl,
          referralLinkId: request.referralLinkId,
          optInToMarketingEmails: optInToMarketingEmails,
        })
      );
    } else {
      // Paid tier: add to cart
      const variant = term.associatedProductVariants?.find((pv) => pv.singleCreated);
      if (!variant) {
        console.error('Invalid membership join request: no product variant found');
        this.clearStoredMembershipJoinRequest();
        dispatch(setJoinInProgress(false));
        return;
      }
      dispatch(
        addMembershipToCart({
          request: variant,
          tierId: request.tier.id,
          referralLinkId: request.referralLinkId,
          optInToMarketingEmails: optInToMarketingEmails,
        })
      );
    }
  }

  /**
   * Store membership join request in localStorage
   *
   * Used to persist request across page reloads (e.g., after Shopify redirect)
   *
   * @param request - The membership join request to store
   */
  private static storeMembershipJoinRequest(request: MembershipJoinRequest): void {
    try {
      localStorage.setItem('membershipJoinRequest', JSON.stringify(request));
    } catch (error) {
      console.error('Failed to store membership join request:', error);
    }
  }

  /**
   * Get stored membership join request from localStorage
   *
   * @returns The stored request, or null if none exists
   */
  static getStoredMembershipJoinRequest(): MembershipJoinRequest | null {
    const requestString = localStorage.getItem('membershipJoinRequest');
    if (requestString && requestString !== '') {
      try {
        return JSON.parse(requestString) as MembershipJoinRequest;
      } catch (error) {
        console.error('Failed to parse stored membership join request:', error);
        this.clearStoredMembershipJoinRequest();
        return null;
      }
    }
    return null;
  }

  /**
   * Clear stored membership join request from localStorage
   */
  static clearStoredMembershipJoinRequest(): void {
    localStorage.removeItem('membershipJoinRequest');
  }

  /**
   * Check if user already has a contract for the specified tier
   *
   * Prevents user from signing up for a tier they already have
   *
   * @param memberships - User's current memberships
   * @param tierId - The tier ID to check
   * @returns True if user has active contract for this tier
   */
  private static userHasContractForTier(memberships: Membership[], tierId: string): boolean {
    if (!memberships || memberships.length === 0) {
      return false;
    }

    const membership = memberships.find((m) => m.tiers.some((tier) => tier.id === tierId));

    return !!membership && !!membership.contract && membership.contract.tier.id === tierId;
  }
}
